import { defineStore } from 'pinia';
import { useStorage } from '@vueuse/core';
import { ref, computed, watch } from 'vue';
import { NotificationType } from '@/common/constants/common.constants';
import i18n from '@/common/i18n';
import { NOTIFICATIONS_CONFIG } from '@/common/constants/notifications.constants';
import appConfig from '@/common/appConfig';
import uniqid from 'uniqid';
import { trackSlowRequestDetected, trackRequestComplete } from '@/common/telemetry';
import { useNotifications } from '@/common/utils/notifications/notificationsPlugin';

type LogSlowRequestParams = {
  id: string;
  url: string;
  method: string;
  startTime: number;
  endTime?: number;
  status?: RequestStatus;
  error?: any;
  isFinished?: boolean;
  isLate?: boolean;
};

type SlowRequestLog = LogSlowRequestParams & {
  duration?: number;
  error?: string;
};

type SlowRequestsStorage = Record<string, SlowRequestLog[]>;

type PendingRequest = {
  id: string;
  url: string;
  method: string;
  startTime: number;
  endTime?: number;
};

type GroupedSlowRequest = {
  url: string;
  method: string;
  count: number;
  averageDelay: number;
  firstSeen: number;
  errors: Array<{ message: string; count: number }>;
};

const STORAGE_KEY = 'slowRequests';

export function normalizeUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    return urlObj.pathname;
  } catch {
    return url.split('?')[0];
  }
}

export function groupRequestsByKey(requests: SlowRequestLog[]): Map<string, SlowRequestLog[]> {
  const groupedMap = new Map<string, SlowRequestLog[]>();

  requests.forEach((request) => {
    const normalizedUrl = normalizeUrl(request.url);
    const key = `${normalizedUrl}:${request.method}`;

    if (!groupedMap.has(key)) {
      groupedMap.set(key, []);
    }
    const group = groupedMap.get(key);
    if (group) {
      group.push(request);
    }
  });

  return groupedMap;
}

export function calculateRequestDurations(requests: SlowRequestLog[], now: number): number[] {
  return requests
    .map((req) => {
      if (req.duration !== undefined) {
        return req.duration;
      }
      if (req.endTime !== undefined) {
        return req.endTime - req.startTime;
      }
      return now - req.startTime;
    })
    .filter((d) => d > 0);
}

export function calculateAverageDelay(durations: number[]): number {
  if (durations.length === 0) {
    return 0;
  }
  return durations.reduce((sum, d) => sum + d, 0) / durations.length;
}

export function extractRequestErrors(requests: SlowRequestLog[]): Array<{ message: string; count: number }> {
  const errorMap = new Map<string, number>();

  requests.forEach((req) => {
    if (req.error) {
      const currentCount = errorMap.get(req.error) || 0;
      errorMap.set(req.error, currentCount + 1);
    }
  });

  return Array.from(errorMap.entries()).map(([message, errorCount]) => ({
    message,
    count: errorCount
  }));
}

export const useSlowRequestsStore = defineStore('slowRequests', () => {
  const { notify } = useNotifications();
  const slowRequests = useStorage<SlowRequestsStorage>(STORAGE_KEY, {}, sessionStorage);
  const pendingRequests = new Map<string, PendingRequest>();
  const requestTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
  const forceRefresh = ref(false);
  const forceRefreshInterval = ref<null | ReturnType<typeof setInterval>>();

  function saveRequest(log: SlowRequestLog): void {
    try {
      const normalizedUrl = normalizeUrl(log.url);
      const currentRequests = slowRequests.value[normalizedUrl] || [];
      slowRequests.value = {
        ...slowRequests.value,
        [normalizedUrl]: [...currentRequests, log]
      };
    } catch {
      console.error('Failed to save slow request log');
    }
  }

  function updateRequest(log: SlowRequestLog): void {
    try {
      const normalizedUrl = normalizeUrl(log.url);
      const currentRequests = slowRequests.value[normalizedUrl] || [];
      const requestIndex = currentRequests.findIndex((req) => req.id === log.id);

      if (requestIndex !== -1) {
        const updatedRequests = [...currentRequests];
        updatedRequests[requestIndex] = log;
        slowRequests.value = {
          ...slowRequests.value,
          [normalizedUrl]: updatedRequests
        };
      }
    } catch {
      console.error('Failed to update slow request log');
    }
  }

  function removeRequest(id: string, url: string): void {
    try {
      const timeoutId = requestTimeouts.get(id);
      if (timeoutId) {
        clearTimeout(timeoutId);
        requestTimeouts.delete(id);
      }

      const normalizedUrl = normalizeUrl(url);
      const currentRequests = slowRequests.value[normalizedUrl] || [];
      const filteredRequests = currentRequests.filter((req) => req.id !== id);

      if (filteredRequests.length === 0) {
        const { [normalizedUrl]: _, ...rest } = slowRequests.value;
        slowRequests.value = rest;
      } else {
        slowRequests.value = {
          ...slowRequests.value,
          [normalizedUrl]: filteredRequests
        };
      }
    } catch {
      console.error('Failed to remove slow request log');
    }
  }

  function logSlowRequest({
    url,
    method
  }: { url: string; method: string }): (status?: RequestStatus, error?: any) => void {
    const id = uniqid();
    const startTime = Date.now();
    const normalizedUrl = normalizeUrl(url);
    const upperMethod = method.toUpperCase();

    const log: SlowRequestLog = {
      id,
      url,
      method: upperMethod,
      startTime,
      isFinished: false,
      isLate: false
    };

    saveRequest(log);

    pendingRequests.set(id, {
      id,
      url,
      method: upperMethod,
      startTime,
    });

    const timeoutId = setTimeout(() => {
      const currentRequests = slowRequests.value[normalizedUrl] || [];
      const existingRequest = currentRequests.find((req) => req.id === id);

      if (existingRequest && !existingRequest.isFinished) {
        const updatedLog: SlowRequestLog = {
          ...existingRequest,
          isLate: true
        };

        updateRequest(updatedLog);

        trackSlowRequestDetected(id, normalizedUrl, upperMethod);
      }

      requestTimeouts.delete(id);
    }, appConfig.slow_request_threshold);

    requestTimeouts.set(id, timeoutId);

    return function stopRequestLog(status?: RequestStatus, error?: any): void {
      const existingTimeoutId = requestTimeouts.get(id);
      if (existingTimeoutId) {
        clearTimeout(existingTimeoutId);
        requestTimeouts.delete(id);
      }

      pendingRequests.delete(id);

      const endTime = Date.now();
      const duration = endTime - startTime;
      const currentRequests = slowRequests.value[normalizedUrl] || [];
      const existingRequest = currentRequests.find((req) => req.id === id);

      if (existingRequest) {
        const isLate = duration >= appConfig.slow_request_threshold || existingRequest.isLate;
        if (isLate) {
          const updatedLog: SlowRequestLog = {
            ...existingRequest,
            endTime,
            duration,
            status,
            error: error ? error.message : undefined,
            isFinished: true,
            isLate: true
          };

          updateRequest(updatedLog);

          trackRequestComplete(
            id,
            normalizedUrl,
            upperMethod,
            status || 'timeout',
            duration
          );
        } else {
          removeRequest(existingRequest.id, url);
        }
      }
    };
  }

  function getSlowRequests(): SlowRequestsStorage {
    return slowRequests.value;
  }

  function getSlowRequestsByUrl(url: string): SlowRequestLog[] {
    const normalizedUrl = normalizeUrl(url);
    return slowRequests.value[normalizedUrl] || [];
  }

  function clearSlowRequests(): void {
    try {
      clearInterval(forceRefreshInterval.value as ReturnType<typeof setInterval>);
      forceRefreshInterval.value = null;

      requestTimeouts.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      requestTimeouts.clear();
      slowRequests.value = {};
    } catch {
      console.error('Failed to clear slow request logs');
    }
  }

  function clearSlowRequestsByUrl(url: string): void {
    try {
      const normalizedUrl = normalizeUrl(url);
      const { [normalizedUrl]: _, ...rest } = slowRequests.value;
      slowRequests.value = rest;
    } catch {
      console.error('Failed to clear slow request logs by URL');
    }
  }

  const allSlowRequestsFlat = computed(() => Object.values(slowRequests.value).flat());
  const lateSlowRequestsFlat = computed(() => allSlowRequestsFlat.value.filter((request) => request.isLate));
  const slowRequestsByUrl = computed(() => (url: string) => getSlowRequestsByUrl(url));
  const pendingRequestsComputed = computed(() => allSlowRequestsFlat.value.filter((request) => !request.isFinished && request.isLate));

  const actuallySlowRequests = computed(() => {
    const now = Date.now();
    return allSlowRequestsFlat.value.filter((request) => {
      if (request.isLate) {
        return true;
      }
      const { duration: requestDuration, endTime, startTime } = request;
      let duration: number;
      if (requestDuration !== undefined) {
        duration = requestDuration;
      } else if (endTime !== undefined) {
        duration = endTime - startTime;
      } else {
        duration = now - startTime;
      }
      return duration >= appConfig.slow_request_threshold;
    });
  });

  const triggerRefresh = () => forceRefresh.value;

  const groupedSlowRequests = computed((): GroupedSlowRequest[] => {
    triggerRefresh();
    const groupedMap = groupRequestsByKey(actuallySlowRequests.value);
    const now = Date.now();

    return Array.from(groupedMap.entries()).map(([key, requests]) => {
      const [normalizedUrl, method] = key.split(':');
      const count = requests.length;
      const durations = calculateRequestDurations(requests, now);
      const averageDelay = calculateAverageDelay(durations);
      const firstSeen = requests.reduce((min, req) => (req.startTime < min ? req.startTime : min), requests[0].startTime);
      const errors = extractRequestErrors(requests);

      return {
        url: normalizedUrl,
        method,
        count,
        averageDelay,
        firstSeen,
        errors
      };
    });
  });

  watch(() => [forceRefresh.value, pendingRequestsComputed.value] as const, async ([_, newVal]) => {
    const currentLength = newVal.length;
    const notificationId = 'slow-requests-notification';
    const minimalRefreshTime = 15000;
    forceRefresh.value = false;

    if (currentLength > 0) {
      if (!forceRefreshInterval.value) {
        forceRefreshInterval.value = setInterval(
          () => {
            forceRefresh.value = true;
          }, minimalRefreshTime
        );
      }
      notify.info({
        id: notificationId,
        message: i18n.t('The operation takes longer than usual'),
        showBottom: true,
        duration: 0,
        showClose: true,
        actions: [{
          label: i18n.t('Details'),
          name: 'showSlowRequestsDetails',
        }],
      });
    } else {
      clearInterval(forceRefreshInterval.value as ReturnType<typeof setInterval>);
      forceRefreshInterval.value = null;

      notify.update({
        id: notificationId,
        duration: NOTIFICATIONS_CONFIG[NotificationType.INFO].duration,
        showClose: true,
      });
    }
  });

  return {
    slowRequests,
    slowRequestsByUrl,
    allSlowRequestsFlat,
    pendingRequestsComputed,
    logSlowRequest,
    getSlowRequests,
    getSlowRequestsByUrl,
    clearSlowRequests,
    clearSlowRequestsByUrl,
    lateSlowRequestsFlat,
    groupedSlowRequests
  };
});

export default useSlowRequestsStore;
